import { Checkbox, Select, Tooltip } from 'antd'
import { useMemo, useState } from 'react'
import ModalConfirm from '@/components/ModalConfirm/ModalConfirm'
import {
  findCheckpointByReference,
  getDependency,
  getTriggerCandidates,
  hasConfiguredDependency,
  isCheckpointUsedAsTrigger,
} from '@/utils/checkpointDependency'
import CheckpointMapOptions from './CheckpointMapOptions'

export function DependsOnCheckbox({
  item,
  sections,
  onToggleDependsOn,
}: {
  item: any
  sections: any[]
  onToggleDependsOn: (enabled: boolean) => void
}) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const dependsOn = Boolean(getDependency(item))
  const usedAsTrigger = useMemo(
    () => isCheckpointUsedAsTrigger(sections, item),
    [sections, item]
  )
  return (
    <>
      <Tooltip
        title={
          usedAsTrigger
            ? 'Another checkpoint depends on this one. Remove that dependency first.'
            : undefined
        }
      >
        <span>
          <Checkbox
            style={{ marginLeft: 16 }}
            checked={dependsOn}
            disabled={usedAsTrigger}
            onChange={(e) => {
              if (!e.target.checked && dependsOn) {
                if (hasConfiguredDependency(item)) {
                  setConfirmOpen(true)
                  return
                }
                onToggleDependsOn(false)
                return
              }
              onToggleDependsOn(e.target.checked)
            }}
          >
            Depends on
          </Checkbox>
        </span>
      </Tooltip>
      <ModalConfirm
        open={confirmOpen}
        title="Remove checkpoint dependency?"
        content={
          <div>All option mappings for this checkpoint will be deleted.</div>
        }
        confirmButtonText="Remove"
        onClose={() => setConfirmOpen(false)}
        onOK={() => {
          onToggleDependsOn(false)
          setConfirmOpen(false)
        }}
      />
    </>
  )
}

type TriggerProps = {
  item: any
  sections: any[]
  sectionIndex: number
  subIndex: number
  checkpointIndex: number
  onChangeDependencyTrigger: (reference: string) => void
}

export function CheckpointDependencyTrigger({
  item,
  sections,
  sectionIndex,
  subIndex,
  checkpointIndex,
  onChangeDependencyTrigger,
}: TriggerProps) {
  const dependency = getDependency(item)

  const triggerCandidates = useMemo(
    () =>
      getTriggerCandidates(sections, sectionIndex, subIndex, checkpointIndex),
    [sections, sectionIndex, subIndex, checkpointIndex]
  )

  if (!dependency) return null

  const reference = dependency.reference
  const isStaleReference =
    Boolean(reference) &&
    !triggerCandidates.some((c) => c.reference === reference)

  const options = [
    ...(isStaleReference && reference
      ? [
          {
            label: <span style={{ color: '#c03261' }}>{reference}</span>,
            value: reference,
            disabled: true,
          },
        ]
      : []),
    ...triggerCandidates.map((c) => ({
      label: c.title || c.name,
      value: c.reference,
    })),
  ]

  return (
    <div className="depend-on-select">
      <Select
        size="large"
        placeholder="Select trigger checkpoint"
        value={reference || undefined}
        status={isStaleReference ? 'error' : undefined}
        onChange={onChangeDependencyTrigger}
        options={options}
      />
    </div>
  )
}

type MapProps = {
  item: any
  sections: any[]
  onToggleMappedOption: (
    triggerValue: string,
    optionValue: string,
    checked: boolean
  ) => void
  onSelectAllMappedOptions: (triggerValue: string) => void
}

export function CheckpointDependencyMapOptions({
  item,
  sections,
  onToggleMappedOption,
  onSelectAllMappedOptions,
}: MapProps) {
  const dependency = getDependency(item)
  const selectedTrigger = useMemo(() => {
    if (!dependency?.reference) return null
    return findCheckpointByReference(sections, dependency.reference)
  }, [sections, dependency?.reference])

  if (!dependency || !selectedTrigger) return null

  return (
    <CheckpointMapOptions
      dependency={dependency}
      triggerDataSource={selectedTrigger.data_source || []}
      dataSource={item.data_source || []}
      onToggleMappedOption={onToggleMappedOption}
      onSelectAllMappedOptions={onSelectAllMappedOptions}
    />
  )
}
